import { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, type KeyboardEvent } from 'react';
import { CheckboxGlyph } from '../components/note/Checkbox';
import { lineOps } from '../notes/model';
import { store } from '../state/store';
import type { Line, Note } from '../storage/schemas';
import type { NoteEditing } from './useNoteEditing';

/** Zero-width space: keeps an empty line one line tall in the auto-size copy. */
const ZWSP = String.fromCharCode(0x200b);

export interface EditableLinesHandle {
  focusLine: (id: string, caret?: number | 'end') => void;
  focusEnd: () => void;
  focusedLineId: () => string | null;
}

interface Props {
  note: Note;
  ink: string;
  editing: NoteEditing;
  onFocusChange?: (lineId: string | null) => void;
  placeholder?: string;
}

export const EditableLines = forwardRef<EditableLinesHandle, Props & { textRef: React.RefObject<HTMLDivElement | null> }>(function EditableLines(
  { note, ink, editing, onFocusChange, placeholder = 'Write something…', textRef },
  ref,
) {
  const inputs = useRef(new Map<string, HTMLTextAreaElement>());
  const pendingFocus = useRef<{ id: string; caret: number | 'end' } | null>(null);
  const focused = useRef<string | null>(null);
  const composing = useRef(false);

  const focusLine = useCallback((id: string, caret: number | 'end' = 'end') => {
    const el = inputs.current.get(id);
    if (!el) {
      pendingFocus.current = { id, caret };
      return;
    }
    el.focus({ preventScroll: false });
    const pos = caret === 'end' ? el.value.length : Math.min(caret, el.value.length);
    el.setSelectionRange(pos, pos);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focusLine,
      focusEnd: () => {
        const last = note.lines[note.lines.length - 1];
        if (last) focusLine(last.id, 'end');
      },
      focusedLineId: () => focused.current,
    }),
    [focusLine, note.lines],
  );

  useLayoutEffect(() => {
    if (pendingFocus.current) {
      const { id, caret } = pendingFocus.current;
      if (inputs.current.has(id)) {
        pendingFocus.current = null;
        focusLine(id, caret);
      }
    }
  });

  const split = (line: Line, caret: number) => {
    // Enter on an empty checklist item leaves the list (Notes-app convention).
    if (line.kind === 'check' && line.text === '' ) {
      editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, line.id, { kind: 'text', checked: false }) }), 'structure');
      return;
    }
    let focusId = '';
    editing.edit((n) => {
      const r = lineOps.splitAt(n.lines, line.id, caret);
      focusId = r.focusId;
      return { ...n, lines: r.lines };
    }, 'structure');
    if (focusId) {
      pendingFocus.current = { id: focusId, caret: 0 };
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>, line: Line, index: number) => {
    if (composing.current || e.nativeEvent.isComposing) return;
    const el = e.currentTarget;
    const caret = el.selectionStart ?? 0;
    const collapsed = el.selectionStart === el.selectionEnd;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      split(line, caret);
    } else if (e.key === 'Backspace' && collapsed && caret === 0) {
      if (line.kind === 'check') {
        e.preventDefault();
        editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, line.id, { kind: 'text', checked: false }) }), 'structure');
      } else if (index > 0) {
        e.preventDefault();
        let target: { id: string; caret: number } | null = null;
        editing.edit((n) => {
          const r = lineOps.mergeWithPrevious(n.lines, line.id);
          if (!r) return n;
          target = { id: r.focusId, caret: r.caret };
          return { ...n, lines: r.lines };
        }, 'structure');
        if (target) pendingFocus.current = target;
      }
    } else if (e.key === 'ArrowUp' && (e.altKey || (collapsed && caret === 0)) && index > 0) {
      e.preventDefault();
      if (e.altKey) editing.edit((n) => ({ ...n, lines: lineOps.move(n.lines, line.id, -1) }), 'structure');
      else focusLine(note.lines[index - 1].id, 'end');
      if (e.altKey) pendingFocus.current = { id: line.id, caret };
    } else if (e.key === 'ArrowDown' && (e.altKey || (collapsed && caret === el.value.length)) && index < note.lines.length - 1) {
      e.preventDefault();
      if (e.altKey) {
        editing.edit((n) => ({ ...n, lines: lineOps.move(n.lines, line.id, 1) }), 'structure');
        pendingFocus.current = { id: line.id, caret };
      } else focusLine(note.lines[index + 1].id, 0);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, line: Line) => {
    const text = e.clipboardData.getData('text/plain');
    if (!text || !/[\r\n]/.test(text)) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    let target: { id: string; caret: number } | null = null;
    editing.edit((n) => {
      const removed = lineOps.update(n.lines, line.id, { text: line.text.slice(0, start) + line.text.slice(end) });
      const r = lineOps.insertText(removed, line.id, start, text);
      target = { id: r.focusId, caret: r.caret };
      return { ...n, lines: r.lines };
    }, 'structure');
    if (target) pendingFocus.current = target;
  };

  const toggle = (line: Line) => {
    editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, line.id, { checked: !line.checked }) }), 'check');
    if (!line.checked) store.afterChecked(note.id, line.id);
  };

  const showPlaceholder = note.lines.length === 1 && note.lines[0].text === '' && note.images.length === 0;

  return (
    <div ref={textRef} className={`nc-text is-editing align-${note.style.align} valign-${note.style.vAlign}`}>
      <div className="nc-lines">
      {note.lines.map((line, i) => {
        const m = line.marks ?? {};
        const cls = [
          'nc-line',
          line.kind === 'check' ? 'is-check' : '',
          line.kind === 'check' && line.checked ? 'is-done' : '',
          m.bold ? 'm-bold' : '',
          m.italic ? 'm-italic' : '',
          m.underline ? 'm-underline' : '',
          m.heading ? 'm-heading' : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div key={line.id} className={cls}>
            {line.kind === 'check' && (
              <button
                type="button"
                className="nc-check"
                role="checkbox"
                aria-checked={line.checked}
                aria-label={`${line.checked ? 'Uncheck' : 'Check'} ${line.text || 'item'}`}
                onPointerDown={(e) => e.preventDefault() /* keep the keyboard where it is */}
                onClick={() => toggle(line)}
              >
                <CheckboxGlyph checked={line.checked} color={ink} />
              </button>
            )}
            {/* The hidden ::after copy of the text sizes the textarea (width and height) with pure CSS. */}
            <span className="nc-autosize" data-value={`${line.text || (showPlaceholder ? placeholder : '')}${ZWSP}`}>
            <textarea
              ref={(el) => {
                if (el) inputs.current.set(line.id, el);
                else inputs.current.delete(line.id);
              }}
              className="nc-line-input nc-line-text"
              rows={1}
              value={line.text}
              spellCheck
              autoCapitalize="sentences"
              enterKeyHint="enter"
              aria-label={line.kind === 'check' ? `Checklist item ${i + 1}` : `Line ${i + 1}`}
              placeholder={showPlaceholder ? placeholder : undefined}
              onChange={(e) => {
                const text = e.target.value;
                // Some mobile keyboards insert "\n" without a keydown we can intercept.
                if (text.includes('\n')) {
                  const caret = text.indexOf('\n');
                  const merged = text.replace(/\n/g, '');
                  editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, line.id, { text: merged }) }), `type:${line.id}`);
                  split({ ...line, text: merged }, caret);
                  return;
                }
                editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, line.id, { text }) }), `type:${line.id}`);
              }}
              onKeyDown={(e) => onKeyDown(e, line, i)}
              onPaste={(e) => onPaste(e, line)}
              onCompositionStart={() => (composing.current = true)}
              onCompositionEnd={() => (composing.current = false)}
              onFocus={() => {
                focused.current = line.id;
                onFocusChange?.(line.id);
              }}
              onBlur={() => {
                if (focused.current === line.id) focused.current = null;
                setTimeout(() => {
                  if (!focused.current) onFocusChange?.(null);
                }, 0);
              }}
            />
            </span>
          </div>
        );
      })}
      </div>
    </div>
  );
});
